import { useState, FormEvent } from 'react';

const ContactSection = () => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value.toString());
    });

    setStatus('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d7br09uhe76khnho4opg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0A1628] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(0,217,255,0.3) 40px, rgba(0,217,255,0.3) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,217,255,0.3) 40px, rgba(0,217,255,0.3) 41px)',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <div>
            <span className="inline-block text-[#00D9FF] text-xs font-bold tracking-widest uppercase mb-4">
              Get In Touch
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              Ready to Level Up
              <br />
              Your Security Skills?
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-10">
              Have questions about our labs, resources, or community? Want to partner with us or report a bug? We&apos;d love to hear from you. Our team typically responds within 24 hours.
            </p>

            {/* Contact Info */}
            <div className="space-y-5">
              {[
                { icon: 'ri-mail-line', label: 'Email Us', value: 'hello@cyberskillhub.io' },
                { icon: 'ri-discord-line', label: 'Discord Community', value: 'discord.gg/cyberskillhub' },
                { icon: 'ri-twitter-x-line', label: 'Follow on X', value: '@CyberSkillHub' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[#00D9FF]/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#00D9FF] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-0.5">{item.label}</div>
                    <div className="text-white text-sm font-semibold">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Community Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4">
              {[
                { value: '50K+', label: 'Members' },
                { value: '12K+', label: 'Forum Posts' },
                { value: '4.9★', label: 'Rating' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                  <div className="text-[#00D9FF] font-black text-xl">{stat.value}</div>
                  <div className="text-gray-500 text-xs mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-8">
            <h3 className="text-[#0A1628] font-black text-xl mb-6">Send Us a Message</h3>

            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-emerald-500 text-3xl"></i>
                </div>
                <h4 className="text-[#0A1628] font-bold text-lg mb-2">Message Sent!</h4>
                <p className="text-gray-500 text-sm">We&apos;ll get back to you within 24 hours.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2 rounded-full bg-[#0A1628] text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#0A1628] text-xs font-semibold mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="John"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#00D9FF] transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0A1628] text-xs font-semibold mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Doe"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#00D9FF] transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#0A1628] text-xs font-semibold mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#00D9FF] transition-colors duration-200"
                  />
                </div>

                <div>
                  <label className="block text-[#0A1628] text-xs font-semibold mb-1.5">
                    Subject
                  </label>
                  <select
                    name="subject"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#0A1628] focus:outline-none focus:border-[#00D9FF] transition-colors duration-200 cursor-pointer"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="labs">Lab Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="bug">Bug Report</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#0A1628] text-xs font-semibold mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us how we can help..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#0A1628] placeholder-gray-400 focus:outline-none focus:border-[#00D9FF] transition-colors duration-200 resize-none"
                  ></textarea>
                  <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                    {charCount}/500
                  </div>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="w-full py-4 rounded-xl bg-[#0A1628] text-white font-bold text-sm hover:bg-[#0F2A4A] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-gray-400 text-xs text-center">
                  By submitting, you agree to our{' '}
                  <a href="#" className="text-[#00D9FF] hover:underline">Privacy Policy</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
